import { createApi } from "@reduxjs/toolkit/query/react";
import RtkBaseQueryWithReauth from "./RtkBaseQueryWithReauth";
import ApiPath from './ConstantApi';
import { setCategoryList } from "../Redux/Slices/SettingSlice";

// Define types for the API
export interface CategoryData {
  parentCategoryName?: string;
  categoryName: string;
  description: string;
  isActive: boolean;
  icon?: string;
  _id?: string;
  isDelete?: boolean;
}

export interface UpdateCategoryRequest {
  id: string;
  data: CategoryData;
}


export interface CategoryResponse {
  _id?: string;
  parentCategoryName?: string;
  categoryName: string;
  description: string;
  isActive: boolean;
  isDelete?: boolean;
  icon?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface SupplierUpdateCategoryRequest {
  id: string;
  data: SupplierCategoryResponse;
}

export interface SupplierCategoryResponse {
  _id?: string;
  categoryName: string;
  isActive: boolean;
}

interface Filter {
  key: string;
  value: any;
}

// Create the API slice
export const CategoryService = createApi({
  reducerPath: "CategorieApis",
  baseQuery: RtkBaseQueryWithReauth(process.env.REACT_APP_BASE_URL || ''),
  tagTypes: ["Categories", "ArchiveCategories"],
  endpoints: (builder) => ({
    addAndUpdateCategory: builder.mutation<CategoryResponse, CategoryData | UpdateCategoryRequest>({
      query: (payload) => {
        // Check if this is an update request (has an id)
        if ('id' in payload) {
          return {
            url: `${ApiPath.updateCategory}/${payload.id}`,
            method: 'PUT',
            body: payload.data,
          };
        }
        // This is a create request
        return {
          url: ApiPath.categoryAdd,
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: ["Categories"],
    }),

    getCategoryList: builder.query<CategoryResponse[], void>({
      query: () => ({
        url: ApiPath.categoryList,
        method: "POST",
        body: {
          "filters": [
            { "key": "isDelete", "value": false },
            { "key": "isActive", "value": true },
          ],
          "page": 1,
          "sort": "desc",
          "pageSize": 10000
        }
      }),
      transformResponse: (response: { data: CategoryResponse[] }) => {
        // If data exists, sort it before returning
        if (response.data && Array.isArray(response.data)) {
          return [...response.data].sort((a, b) => {
            const dateA = new Date(a.createdAt).getTime();
            const dateB = new Date(b.createdAt).getTime();
            return dateB - dateA; // Descending order
          });
        }
        return [];
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Data is already sorted from transformResponse
          dispatch(setCategoryList({ categoryList: data }));
        } catch (error) {
          dispatch(setCategoryList({ categoryList: [] }));
        }
      },
      providesTags: ["Categories"],
    }),

    getAllFilterCategoryList: builder.query<CategoryResponse[], { filters?: Filter[] }>({
      query: (arg) => {
        const filters = arg?.filters ?? [{ key: "isDelete", value: false }];
        return {
          url: ApiPath.categoryList,
          method: "POST",
          body: {
            "filters": filters,
            "page": 1,
            "sort": "desc",
            "pageSize": 10000
          }
        }
      },
      transformResponse: (response: { data: CategoryResponse[] }) => response.data,
      providesTags: ["ArchiveCategories"],
    }),

    getAllDynamicFilterCategoryList: builder.query<{ data: CategoryResponse[], totalRecords: number }, { filters?: Filter[], pageSize?: number, page?: number, sortBy?: string, sortOrder?: string }>({
      query: (arg) => {
        const filters = arg?.filters ?? [{ key: "isDelete", value: false }];
        return {
          url: ApiPath.categoryList,
          method: "POST",
          body: {
            filters,
            page: arg.page,
            pageSize: arg.pageSize,
            sortBy: arg.sortBy || undefined,
            sortOrder: arg.sortOrder || "desc",
          }
        }
      },
      providesTags: ["Categories"],
    }),

    searchCategory: builder.query<any, string>({
      query: (searchString) => ({
        url: `${ApiPath.categorySearch}?name=${searchString}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: any }) => response.data,
    }),

    getCategoryProductCount: builder.query<{ count: number }, string>({
      query: (id) => ({
        url: `/category/${id}/product-count`,
        method: 'GET',
      }),
      transformResponse: (response: { status: string; count: number }) => ({ count: response.count }),
    }),

    deleteCategory: builder.mutation<void, { id: string, isDelete: boolean }>({
      query: (payload) => ({
        url: `${ApiPath.deleteCategory}/${payload.id}`,
        method: 'DELETE',
        body: {
          "isDelete": payload.isDelete
        }
      }),
      invalidatesTags: ['Categories', 'ArchiveCategories'],
    }),

    /**@Supplier_Category_List */
    addAndUpdateSupplierCategory: builder.mutation<SupplierCategoryResponse, SupplierUpdateCategoryRequest | any>({
      query: (payload) => {
        // Check if this is an update request (has an id)
        if ('id' in payload) {
          return {
            url: `${ApiPath.updateSupplierCategory}/${payload.id}`,
            method: 'PUT',
            body: payload.data,
          };
        }
        // This is a create request
        return {
          url: ApiPath.supplierCategoryAdd,
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: ["Categories"],
    }),

    getSupplierCategoryList: builder.query<SupplierCategoryResponse[], void>({
      query: () => ({
        url: ApiPath.supplierCategoryList,
        method: "POST",
        body: {
          "filters": [
            { "key": "isDelete", "value": false }
          ],
          "page": 1,
          "pageSize": 10000
        }
      }),
      transformResponse: (response: { data: SupplierCategoryResponse[] }) => response.data,
      providesTags: ["Categories"],
    }),

    deleteSupplierCategory: builder.mutation<void, string>({
      query: (id) => ({
        url: `${ApiPath.deleteSupplierCategory}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Categories'],
    }),
  }),
});

// Export hooks for the API
export const {
  useGetCategoryListQuery,
  useLazyGetAllFilterCategoryListQuery,
  useLazyGetAllDynamicFilterCategoryListQuery,
  useLazyGetCategoryListQuery,
  useAddAndUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useLazySearchCategoryQuery,
  useLazyGetCategoryProductCountQuery,

  useGetSupplierCategoryListQuery, /**@Supplier_Category_List */
  useAddAndUpdateSupplierCategoryMutation,
  useDeleteSupplierCategoryMutation,
} = CategoryService;
